import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Badge, CardActions, Cluster, TaskCard } from '../ui';
import {
  activationChecklistKeys,
  dismissActivationChecklist,
  getActivationChecklist,
  type ActivationChecklistStep,
} from '../../lib/api/activationChecklist.gen';
import { useAuth } from '../../lib/auth';

// The persisted first-value activation checklist (P16 §6.1, WGTM step 1/11;
// story 2-106) — the operational replacement for the retired `OnboardingTour`
// (intro.js force-navigation route-pin walkthrough, deleted this story).
//
// ═══════════════════════════════════════════════════════════════════════════
// NON-BLOCKING, BY DESIGN
// ═══════════════════════════════════════════════════════════════════════════
// The done-when's central correctness claim is that the old tour's forced
// navigation is gone: a fresh, un-onboarded user can navigate anywhere in the
// app immediately, at any time. This widget is a plain card on the dashboard
// — no overlay, no `navigate(..., {replace:true})` effect, nothing that reacts
// to the CURRENT route. Every step's `to` is a normal link a person clicks
// (`navigate()` on demand), never something this component forces on mount.
//
// ═══════════════════════════════════════════════════════════════════════════
// WHY IT DISAPPEARS ON ITS OWN ONCE EVERYTHING IS DONE
// ═══════════════════════════════════════════════════════════════════════════
// Once every step is a real, durable fact (`allCompleted`), there is nothing
// left to remedy — showing a permanent "you're all set!" banner would be the
// same class of invented-affordance noise WGTM step 1 forbids for the
// dashboard's KPIs. So this card auto-hides on `allCompleted` without
// requiring an explicit dismiss click; the EXPLICIT "Skip setup" affordance
// below exists for the OTHER case — a principal who wants it gone before
// finishing, which is the only case the done-when's "skip/dismiss without
// reappearing" clause is actually about.
export function ActivationChecklistCard(): JSX.Element | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // G-18 warns any plain `useQuery` to consider `useCursorList()` — that gate
  // is for a PAGED list. `GET /v1/activation-checklist` answers one atomic
  // object (six fixed steps), not a cursor envelope, so there is no list to
  // page; a plain `useQuery` is correct here, same reasoning `DashboardPage`'s
  // own `/v1/home` read documents for the identical warning.
  const { data, isLoading, isError } = useQuery({
    queryKey: activationChecklistKeys.list({ org: user?.organizationId }),
    queryFn: () => getActivationChecklist(),
    enabled: Boolean(user),
    // Every step is derived fresh from real records on every read (this
    // file's own header) — a short staleTime is enough to avoid a refetch on
    // every re-render while still reconciling promptly after a real mutation
    // elsewhere (e.g. creating a Site) invalidates this key, or a background
    // refetch (`refetchOnWindowFocus`) picks up another tab's progress or an
    // org switch. No manual poll: this is a courtesy nudge, not live data.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissActivationChecklist(),
    onSuccess: (next) => {
      queryClient.setQueryData(activationChecklistKeys.list({ org: user?.organizationId }), next);
    },
  });

  if (!user || isLoading || isError || !data) return null;
  // Durable dismissal (server-persisted, resumes across session/device) or
  // nothing left to do — either way, nothing to show.
  if (data.dismissed || data.allCompleted) return null;

  const accentFor = (step: ActivationChecklistStep): 'success' | 'primary' | 'neutral' => {
    if (step.completed) return 'success';
    return step.allowed ? 'primary' : 'neutral';
  };

  return (
    <div className="rounded-hairline border border-border bg-card space-y-4 p-6" data-testid="activation-checklist">
      <Cluster justify="between" gap={3}>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Get set up</h2>
          <p className="text-sm text-muted-foreground">
            Six steps from your first Site to sharing a report — pick up wherever you left off.
          </p>
        </div>
        <CardActions
          actions={[
            {
              id: 'dismiss',
              label: 'Skip setup',
              variant: 'ghost',
              loading: dismissMutation.isPending,
              onClick: () => dismissMutation.mutate(),
              'data-testid': 'activation-checklist-dismiss',
            },
          ]}
        />
      </Cluster>

      <div className="space-y-3">
        {data.steps.map((step) => (
          <TaskCard
            key={step.key}
            data-testid={`activation-checklist-${step.key}`}
            title={step.title}
            subtitle={step.description}
            accent={accentFor(step)}
            {...(step.to ? { onOpen: () => navigate(step.to as string), openLabel: `Go to ${step.title}` } : {})}
            notice={step.reason ?? undefined}
            action={
              step.completed ? (
                <Badge tone="success">
                  <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden />
                  Done
                </Badge>
              ) : !step.allowed ? (
                <Badge tone="neutral">
                  <LockClosedIcon className="h-3.5 w-3.5" aria-hidden />
                  Not your role
                </Badge>
              ) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}
